import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Star, ArrowLeft, Lock, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password. Please try again.';
      toast.error(message);
      if (message.includes('expired')) {
        setError('This reset link has expired. Please request a new one.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle)] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-[var(--color-text-secondary)] hover:text-primary mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>
        
        <Card className="shadow-layered border-0">
          <CardHeader className="text-center pb-2">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${
              error ? 'bg-red-500' : success ? 'bg-green-500' : 'bg-primary'
            }`}>
              {error ? (
                <AlertCircle className="w-7 h-7 text-white" />
              ) : success ? (
                <Check className="w-7 h-7 text-white" />
              ) : (
                <Lock className="w-7 h-7 text-white" />
              )}
            </div>
            <CardTitle className="font-dashboard-heading text-2xl font-bold">
              {error ? 'Link Invalid' : success ? 'Password Reset!' : 'Reset Password'}
            </CardTitle>
            <CardDescription className="text-[var(--color-text-muted)]">
              {error ? 'This reset link is no longer valid' : success ? 'Your password has been changed' : 'Enter your new password'}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {error ? (
              <div className="text-center space-y-4">
                <p className="text-[var(--color-text-secondary)]">{error}</p>
                <Link to="/forgot-password">
                  <Button className="w-full rounded-full">
                    Request New Link
                  </Button>
                </Link>
              </div>
            ) : success ? (
              <div className="text-center space-y-4">
                <p className="text-[var(--color-text-secondary)]">
                  You can now log in with your new password.
                </p>
                <Link to="/login">
                  <Button className="w-full rounded-full">
                    Go to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="reset-password-input"
                    className="h-11"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="reset-confirm-input"
                    className="h-11"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full h-11 rounded-full btn-lift"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
